import {
  bodyKeys,
  bodyLabels,
  calculateMonthlyPayment,
  consultTimeKeys,
  consultTimeLabels,
  consultTopicKeys,
  consultTopicLabels,
  fuelKeys,
  fuelLabels,
  plateKindLabels,
  plateKindPrice,
  plateKinds,
  platePriceWords,
  priceAmount,
  primaryPlateKind,
  sortKeys,
  specGroups,
  specKeys,
  specLabels,
  type BodyKey,
  type ConsultTimeKey,
  type ConsultTopicKey,
  type FuelKey,
  type HomepageSectionKey,
  type PlateKind,
  type PlatePrice,
  type SortKey,
  type SpecKey,
} from "@car/api/catalog-config";
import type { AppRouterClient } from "@car/api/routers/index";
import {
  fillText,
  financeCalculator,
  footerMenu,
  footerSeals,
  headerMenu,
  lines,
  mobileBar,
  pageSections,
  readSiteContent,
  serviceCards,
  telHref,
  type MenuLink,
  type MobileBarIcon,
  type SiteContent,
  type SitePage,
} from "@car/api/site-content";
import type { QueryClient } from "@tanstack/react-query";
import { rootRouteId, useLoaderData, useLocation } from "@tanstack/react-router";

import { faNumber, faPercent, faToman } from "@/lib/format";
import { orpc } from "@/utils/orpc";

// ---------------------------------------------------------------------------
// سایت عمومی: کلیدها و برچسب‌ها از سرور (@car/api/catalog-config) می‌آیند و
// نوع داده‌ها از خروجی خود API گرفته می‌شود.
// ---------------------------------------------------------------------------

export {
  bodyKeys,
  bodyLabels,
  calculateMonthlyPayment,
  consultTimeKeys,
  consultTimeLabels,
  consultTopicKeys,
  consultTopicLabels,
  fuelKeys,
  fuelLabels,
  plateKindLabels,
  plateKindPrice,
  plateKinds,
  platePriceWords,
  priceAmount,
  primaryPlateKind,
  sortKeys,
  specGroups,
  specKeys,
  specLabels,
};
export type {
  BodyKey,
  ConsultTimeKey,
  ConsultTopicKey,
  FuelKey,
  HomepageSectionKey,
  MenuLink,
  MobileBarIcon,
  PlateKind,
  PlatePrice,
  SiteContent,
  SitePage,
  SortKey,
  SpecKey,
};
export {
  fillText,
  financeCalculator,
  footerMenu,
  footerSeals,
  headerMenu,
  lines,
  mobileBar,
  pageSections,
  serviceCards,
  telHref,
};

/** A menu link's attributes: another site opens in a new tab, without access to this one */
export const linkAttrs = (link: MenuLink) =>
  link.external
    ? { href: link.href, target: "_blank", rel: "noopener noreferrer" }
    : { href: link.href };

/**
 * Tells whether a menu link points at the page being shown: same path and query (so
 * «/vehicles?fuel=electric» is not «/vehicles»); an in-page anchor like «/#tools» never is.
 */
export function useIsCurrent() {
  const here = useLocation({ select: (location) => location.pathname + location.searchStr });
  return (link: MenuLink) => !link.external && !link.href.includes("#") && link.href === here;
}

/**
 * Staff-edited texts and images (site.content) for a route loader: from the query cache
 * after the first page, and the texts the site ships with when the API cannot be reached,
 * so no page ever fails for want of its copy.
 */
export const loadSiteContent = (queryClient: QueryClient) =>
  queryClient
    .ensureQueryData(orpc.site.content.queryOptions())
    // an API older than this build lacks new blocks/fields: merge its answer over the defaults
    .then((content) =>
      readSiteContent(Object.entries(content).map(([key, value]) => ({ key, value }))),
    )
    .catch((error: Error) => {
      console.error("[site.content]", error);
      return readSiteContent([]);
    });

/** The texts and images every component reads; the root route loads them before any render */
export const useSiteContent = () => useLoaderData({ from: rootRouteId });

type Site = AppRouterClient["site"];

export type HomeView = Awaited<ReturnType<Site["home"]>>;
export type SiteVehicle = Awaited<ReturnType<Site["vehicles"]["list"]>>["vehicles"][number];
/** a sales plan offered on one model (site.vehicles.byId), at its own yearly rate */
export type SitePlan = Awaited<ReturnType<Site["vehicles"]["byId"]>>["plans"][number];
export type SiteBranch = HomeView["branches"][number];
export type Guide = HomeView["guides"][number];
export type Review = HomeView["reviews"][number];
export type SiteBanner = HomeView["banners"][number];

export const isBodyKey = (value: string): value is BodyKey => bodyKeys.some((key) => key === value);
export const isSortKey = (value: string): value is SortKey => sortKeys.some((key) => key === value);
export const isConsultTopic = (value: string): value is ConsultTopicKey =>
  consultTopicKeys.some((key) => key === value);
export const isConsultTime = (value: string): value is ConsultTimeKey =>
  consultTimeKeys.some((key) => key === value);

/** زیرنویس هر ماشین‌حساب اقساط: نرخ سالانه‌ی سایت و توضیح مدیر محتوا (اگر خالی نباشد) */
export const rateNote = (annualRate: number, disclaimer: string) =>
  [`محاسبه بر اساس نرخ سالانه ${faPercent(annualRate)}.`, disclaimer].filter(Boolean).join(" ");

/**
 * a model's «اقساط از» for comparison tables, with the plate its price is quoted with (the
 * compare page lists both plate prices); «بدون طرح اقساط» when no installment plan is offered on
 * it, «استعلام قیمت» when its price is on request (no number to compute installments from)
 */
export const monthlyFromText = (vehicle: SiteVehicle) =>
  vehicle.monthlyFrom !== null
    ? `${faNumber(vehicle.monthlyFrom)} تومان ${primaryPlateText(vehicle)}`
    : priceAmount(vehicle.price) === null
      ? platePriceWords(vehicle.price)
      : "بدون طرح اقساط";

/**
 * the exact digits beside platePriceWords («۲٬۸۵۰٬۰۰۰٬۰۰۰ تومان»), for a title attribute; none
 * for a price on request
 */
export const platePriceTitle = (price: PlatePrice) => {
  const amount = priceAmount(price);
  return amount === null ? undefined : faToman(amount);
};

/** «با پلاک ملی»: the plate a vehicle's list `price` is quoted with (the /vehicles plate filter's, else the primary one) */
export const primaryPlateText = (vehicle: SiteVehicle) =>
  `با ${plateKindLabels[vehicle.pricePlate]}`;
